import { DocumentIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import FileBase from '../core/form/FileBase';
import FileIndicator from '../core/FileIndicator';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import ModuleBaseForm from './ModuleBaseForm';

const ImportModelForm = ({ isUploading, errorMessage, onFileChange }) => {
  const { setValue, watch } = useFormContext();
  const selectedFile = watch('file');

  const handleFileChange = async files => {
    const newFile = files[0];
    setValue('file', newFile);
    if (onFileChange) {
      onFileChange(newFile);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm">
        Sesame allows users to create their own module by importing a custom Excel spreadsheet. Two sheets should be
        added to your spreadsheet and formatted according to our guidelines. Download the sheets template below and
        integrate them into your model following the guidelines outlined in the dropdown.
      </p>

      <ModuleBaseForm />

      <div className="flex flex-col">
        {selectedFile ? (
          <FileIndicator file={selectedFile} onRemove={() => handleFileChange(null)} />
        ) : (
          <FileBase
            name="file"
            className="text-md"
            onUpload={handleFileChange}
            onDelete={() => handleFileChange(null)}
            acceptedFormats=".xlsx"
            label="a custom model"
            isUploading={isUploading}
          />
        )}
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="text-gray-600">
            <DocumentIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="font-medium text-sm">File Template</div>
            <div className="text-xs text-gray-500">XLSX</div>
          </div>
        </div>

        <a
          href="https://docs.sesame.software/data_upload/excel_upload_template.xlsx"
          target="_parent"
          download
          className="btn btn-sm gap-2 bg-white shadow-sm hover:shadow rounded-lg border border-gray-200 text-gray-600"
          rel="noreferrer"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          Download
        </a>
      </div>

      <details className="collapse collapse-arrow border border-gray-200 rounded-lg">
        <summary className="collapse-title font-medium">
          How to import your own model and create a custom Sesame module
        </summary>
        <div className="collapse-content">
          <ul className="list-decimal space-y-2 pl-4">
            <li>Download the template Excel file</li>
            <li>
              Fill in the sheets to connect to your model. The full guidelines are available{' '}
              <a
                href="https://docs.sesame.software/user-guides/tutorials/excel.html"
                target="_blank"
                rel="noreferrer"
                className="text-purple-500 underline hover:text-purple-400"
              >
                in the documentation
              </a>
            </li>
            <li>Upload your Excel model</li>
            <li>
              Your Excel will be converted into a Sesame module, which you can find under the &quot;Custom&ldquo; tab in
              the palette
            </li>
          </ul>
        </div>
      </details>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="font-medium text-red-700 text-sm mb-2">
            File is not convertible into a Sesame module for the following reason:
          </p>
          <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
            {errorMessage?.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
          <p className="text-red-700 text-sm mt-2">Please correct the issue(s) and re-upload the file.</p>
        </div>
      )}
    </div>
  );
};

ImportModelForm.propTypes = {
  isUploading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.arrayOf(PropTypes.string),
  onFileChange: PropTypes.func,
};

export default ImportModelForm;
